import api from '../api/axios';
import { Post, EhcpTag, TextSource, AiMetadata, DeletedPost, DeletedUpload, RestorePostResult } from '../models/post';
import { TaxonomyPayload } from '../models/taxonomy';

/** True when a restore failed because the grace window has already closed —
 *  the files are gone, so there is nothing to retry. */
export const isPastRestoreWindow = (err: any): { deletedAt?: string; graceDays?: number } | null =>
  err?.response?.status === 410 ? (err.response.data ?? {}) : null;

/** Why an enrichment run produced what it produced. Mirrors the API's
 *  EmbeddingOutcome union (embeddingService.ts). */
export type EnrichOutcome =
  | 'enriched'
  | 'skipped_disabled'
  | 'skipped_not_found'
  | 'skipped_no_text'
  | 'skipped_unchanged'
  | 'skipped_heuristic'
  | 'skipped_debounce'
  | 'skipped_triage';

/** What an enrich request resolves to — see PostDataService.enrich. */
export interface EnrichResult {
  outcome: EnrichOutcome | null;
  metadata: AiMetadata | null;
}

export class PostDataService {
  // deletedOnly swaps to the restore view: the same posts, fully rendered with
  // their photographs, so someone can read one to decide whether they want it
  // back. The date window does not apply there — deleted_at bounds the set.
  static async getPosts(childId: string, fromDate?: string | null, deletedOnly = false): Promise<Post[]> {
    let url = `/posts?childId=${childId}`;
    if (deletedOnly) url += '&deleted=true';
    if (fromDate) {
      // Ensure fromDate is passed as ISO string (YYYY-MM-DD or full ISO)
      url += `&fromDate=${encodeURIComponent(fromDate)}`;
    }
    const response = await api.get(url);
    return response.data;
  }

  // expectedUpdatedAt opts into the optimistic-concurrency check: pass the
  // updated_at the post was loaded with; the API returns 409 POST_MODIFIED if
  // someone else saved since (use getPostConflict). Omit it for last-write-wins.
  static async updatePost(id: number, post: Post, expectedUpdatedAt?: string): Promise<Post> {
    const response = await api.put(`/posts/${id}`, { ...post, expectedUpdatedAt });
    return response.data;
  }

  // Review-and-publish: writes the human-approved record (text + provenance +
  // EHCP categories) and publishes. Reporting-role only (enforced server-side).
  static async publish(
    postId: number,
    payload: {
      finalText: string;
      textSource: TextSource;
      sourceEdited: boolean;
      finalCategories: EhcpTag[];
      expectedUpdatedAt?: string;
    }
  ): Promise<void> {
    await api.post(`/posts/${postId}/publish`, payload);
  }

  // Lazy AI enrichment for the review screen: generates the rewrite + tag
  // suggestions on demand (enrich-if-stale — a no-op if the text is unchanged
  // since the last run) and returns the fresh, desanitised ai_metadata so the
  // overlay can populate without a full reload. Reporting-role only (server-side).
  // ai_metadata is null when there's nothing to suggest (e.g. empty text) — the
  // outcome says which of the several reasons applied, so the review screen can
  // tell "AI is switched off" from "the AI had nothing to add" instead of
  // showing one neutral line for both.
  //
  // Concurrent calls for the same post are coalesced onto one request. The
  // server's content-hash dedup can't help here: two requests issued before
  // either has stored its hash both read the old one, so both pay for a
  // Bedrock run. React StrictMode double-invokes the review overlay's on-open
  // effect in development, which was doing exactly that — two enrichments per
  // post, milliseconds apart, one of them wasted.
  //
  // In-flight only, never a result cache: the entry is dropped the moment the
  // request settles, so a later call always hits the server (which is what
  // makes "Re-run AI" and reopening the overlay still work).
  private static enrichInFlight = new Map<number, Promise<EnrichResult>>();

  static async enrich(postId: number): Promise<EnrichResult> {
    const inFlight = PostDataService.enrichInFlight;
    const existing = inFlight.get(postId);
    if (existing) return existing;

    const request = api.post(`/posts/${postId}/enrich`)
      .then(response => ({
        outcome: (response.data?.outcome ?? null) as EnrichOutcome | null,
        metadata: (response.data?.ai_metadata ?? null) as AiMetadata | null,
      }))
      // Runs on rejection too, so a failed request never wedges the post.
      .finally(() => { inFlight.delete(postId); });

    inFlight.set(postId, request);
    return request;
  }

  // Controlled vocabulary (EHCP domains, safeguarding, staff actions) for the
  // review screen's tag toggles + tooltips.
  static async getTaxonomy(): Promise<TaxonomyPayload> {
    const response = await api.get('/posts/ai/taxonomy');
    return response.data;
  }

  static async createPost(post: Post): Promise<Post> {
    const response = await api.post(`/posts`, post);
    return response.data;
  }

  static async deletePost(id: number): Promise<void> {
    await api.delete(`/posts/${id}`);
  }

  // ── Deletion is reversible, for a while ──────────────────────────────────
  // Deleting a post or an image starts a 7-day clock; after that a scheduled
  // sweep destroys the S3 files and the rows for good. Everything below exists
  // to give the user a way back inside that window — without it, a misclick is
  // permanent, which is worse than the old behaviour where nothing was ever
  // really deleted.
  //
  // Both restore calls return 410 once the window has closed. That is not an
  // error to swallow: the files are already gone, and the user needs to be told
  // that plainly rather than shown a generic failure they might retry.

  // childId scopes the listing to one child, matching what the restore view
  // itself shows — getPosts(deletedOnly) is keyed on the child, so a count
  // taken across the organisation would offer a way back to posts that view
  // will not list. Omitted, it still answers for the whole organisation.
  static async getDeletedPosts(childId?: string): Promise<{ graceDays: number; posts: DeletedPost[] }> {
    const response = await api.get('/posts/deleted', {
      params: childId ? { childId } : undefined,
    });
    return response.data;
  }

  static async restorePost(id: number): Promise<RestorePostResult> {
    const response = await api.post(`/posts/${id}/restore`);
    return response.data;
  }

  static async getDeletedUploads(postId: number): Promise<{ graceDays: number; uploads: DeletedUpload[] }> {
    const response = await api.get(`/posts/${postId}/uploads/deleted`);
    return response.data;
  }

  static async restoreUpload(postId: number, uploadId: number): Promise<void> {
    await api.post(`/posts/${postId}/uploads/${uploadId}/restore`);
  }

  static async unpublish(postId: number): Promise<void> {
    await api.post(`/posts/${postId}/unpublish`);
  }

  // AI typo/grammar correction. Pass `postId` for a saved post (the
  // server then enforces ownership / whitelist / published-status
  // checks against the persisted row). Omit it for an unsaved draft —
  // `childIds` is required in that case for whitelist scoping and PII
  // sanitisation.
  static async correctText(
    text: string,
    options: { postId?: number; childIds?: number[]; signal?: AbortSignal } = {}
  ): Promise<string> {
    const { postId, childIds, signal } = options;
    const response = await api.post(
      '/posts/correct',
      { text, postId, childIds },
      { signal }
    );
    return response.data.corrected_text ?? '';
  }
}

export interface PostConflict {
  updated_at: string;
  updated_by_name: string | null;
}

// Detects the optimistic-concurrency 409 from updatePost/publish so the caller
// can prompt the user to reload. Returns null for any other error.
export function getPostConflict(err: unknown): PostConflict | null {
  const r = (err as any)?.response;
  if (r?.status === 409 && r.data?.error === 'POST_MODIFIED') {
    return { updated_at: r.data.updated_at, updated_by_name: r.data.updated_by_name ?? null };
  }
  return null;
}

